import { uniq, uniqBy } from "lodash-es";

import type { DictsysInflectionScheme, DictsysInflectionSchemeCell, DictsysInflectionSchemeContent } from "@/types";

export function parseJsonInflectionScheme(inflectionScheme: DictsysInflectionScheme): void {
  if (!inflectionScheme.rawJson) {
    return;
  }

  inflectionScheme.json = JSON.parse(inflectionScheme.rawJson);
}

/**
 *
 * @param input Inflection scheme input
 * @param prefixMode Spanish needs to show prefixes for all alternatives for imperative tenses, as they might contain exclamation points or "no"
 */
export function getBodyCell(
  input: DictsysInflectionSchemeContent[] | null,
  prefixMode: "first" | "always" = "first"
): DictsysInflectionSchemeCell {
  const cell: DictsysInflectionSchemeCell = { type: "body", elements: [{ texts: ["-"] }] };

  if (input === null || input.length === 0) {
    return cell;
  }

  const uniqueInflections = uniqBy(input, "inflexion");
  const firstPrefix = uniqueInflections[0].prefix;

  const texts = uniqueInflections.reduce<string[]>((textParts: string[], content: DictsysInflectionSchemeContent) => {
    let prefix = "";

    if (prefixMode === "always" || textParts.length === 0 || firstPrefix !== content.prefix) {
      // French d' prefixes, which should not have space
      if (content.prefix.includes("d'")) {
        prefix = content.prefix;
      } else {
        prefix = content.prefix + " "; // Prefixes do not contain spacing as suffixes do
      }
    }

    return [...textParts, prefix + content.inflexion + content.suffix];
  }, []);

  if (texts.length === 0) {
    return cell;
  }

  cell.elements = texts.map((text) => ({ texts: [text] }));
  return cell;
}

export function getBodyCellWithAuxiliaryVerbs(
  input: DictsysInflectionSchemeContent[] | null
): DictsysInflectionSchemeCell {
  const cell: DictsysInflectionSchemeCell = { type: "body", elements: [{ texts: ["-"] }] };

  if (input === null || input.length === 0) {
    return cell;
  }

  const uniqueInflexions = uniq(input.map((content) => content.inflexion + content.suffix));

  if (uniqueInflexions.length === 1) {
    cell.elements = [
      {
        auxiliaryVerbs: uniq(input.map((content) => content.prefix)).filter((p) => Boolean(p)),
        texts: [uniqueInflexions[0]]
      }
    ];

    return cell;
  }

  cell.elements = uniq(input.map((content) => content.prefix + " " + content.inflexion + content.suffix)).map(
    (text) => ({ texts: [text] })
  );

  return cell;
}
